import _ from 'lodash';

import { isSecondaryDimensionAInEffect } from 'components/V5GadgetForm/isSecondaryDimensionAEligible';

import { canCompare } from '../blockComparison';
import { withComparableBulletTargets } from '../buildBulletSeries';
import buildChartDefinition from '../buildChartDefinition';
import { readDimension } from '../dimensions';
import { guessChartOptions } from '../chartOptions';
import { supportsSeriesVariants, toQueryOverrides } from '../seriesVariants';
import { VIS_TYPE_BY_GADGET_TYPE } from '../visTypes';
import {
  toComparable,
  withoutInertValues,
  withoutMetriclessSeries,
} from './legacyDefaults';
import { guessTableOptions } from './tableMatching';

// Studio's vis type for each gadget type it draws, both of a pair included
// (see getGadgetType). Number blocks are saved as 'card' gadgets.
export const getVisTypeForGadgetType = (
  gadgetType: GadgetType,
): Studio.VisType | undefined => VIS_TYPE_BY_GADGET_TYPE[gadgetType];

// The chart definition a block saves as. Number blocks aren't drawn through
// V5Gadget, but still save as a 'card' gadget with a series per metric, so
// everything that reads gadgets (usage lookups, Copy Content, MCP, Insights)
// sees their metrics. Compared, they save as a 'smartCard', which shows the
// change as a badge (absent show means the smart card's default, % change).
// Following the report's comparison, they're a smart card without one of
// their own, which shows the report's.
export const buildGadgetChartDefinition = ({
  card,
  id,
  name,
}: {
  card: Studio.GadgetSidecarCard;
  id: string;
  name: string;
}): { chartDefinition: V5ChartDefinition } | { missing: string } => {
  if ((card.visType ?? 'number') !== 'number') {
    return buildChartDefinition({ card: { ...card, id }, name });
  }
  if ((card.metricIds ?? []).length === 0) {
    return { missing: 'Add a metric' };
  }
  // The "Out of" metric too, when it isn't one already
  const metricIds = _.uniq([
    ...(card.metricIds ?? []),
    ...(card.denominatorMetricId ? [card.denominatorMetricId] : []),
  ]);
  const comparison = canCompare(card) ? card.comparison : undefined;
  return {
    chartDefinition: {
      id,
      name,
      description: card.description,
      gadgetType: comparison ? 'smartCard' : 'card',
      series: metricIds.map((metricId, index) => ({
        id: `${id}-${metricId}`,
        metricId,
        ...(index === 0 && comparison && comparison.show
          ? { smartCardComparison: comparison.show }
          : {}),
      })),
    },
  };
};

// A simple grid's columns, as it shows them: without aliases (grids saved
// before them) each field is headed by its start-cased name
const getGridColumns = (
  config: SimpleGridConfig,
): Studio.TableRecordsColumn[] =>
  config.fieldAliases ??
  config.fields.map((field) => ({ field, alias: _.startCase(field) }));

// What a simple grid shows: its name, description and grid. The classic
// editor writes every other chart setting too, at defaults that don't apply.
// Columns are in their aliases' order, as the grid and its export show them
// (reordering them in the classic editor only reorders the aliases).
export const toGridShown = (def: V5ChartDefinition): V5ChartDefinition => {
  const config = def.simpleGridConfig;
  const columns = config && getGridColumns(config);
  return {
    id: def.id,
    name: def.name,
    description: def.description || undefined,
    gadgetType: def.gadgetType,
    series: [],
    simpleGridConfig: config && {
      dataType: config.dataType,
      fields: (columns ?? []).map((c) => c.field),
      fieldAliases: columns,
      sort: config.sort ?? [],
      disableLinks: config.disableLinks || undefined,
      gridSizing:
        config.gridSizing === 'fitGadget' ? undefined : config.gridSizing,
    },
  };
};

// As stored in Firestore: without empty values (withoutNulls drops nulls and
// Firestore drops undefined), and without ids, which Studio derives from the
// block rather than keeping. The classic editor's ways of saving what Studio
// saves differently are made the same (see toComparable).
const normalise = (chartDef: V5ChartDefinition): unknown => {
  const def =
    chartDef.gadgetType === 'simpleGrid'
      ? toGridShown(chartDef)
      : toComparable(withComparableBulletTargets(chartDef));
  const { id, series, ...rest } = def;
  return withoutInertValues(
    JSON.parse(
      JSON.stringify({
        ...rest,
        series: series.map(({ id: seriesId, ...s }) => s),
      }),
    ),
  );
};

export const isSameChart = ({
  a,
  b,
}: {
  a: V5ChartDefinition;
  b: V5ChartDefinition;
}): boolean => _.isEqual(normalise(a), normalise(b));

// Whether building the chart from these settings gives exactly the stored
// definition, i.e. Studio can edit it without losing anything
export const reproducesChart = ({
  card,
  chartDef,
}: {
  card: Studio.GadgetSidecarCard;
  chartDef: V5ChartDefinition;
}): boolean => {
  const built = buildGadgetChartDefinition({
    card,
    id: chartDef.id,
    name: chartDef.name,
  });
  return (
    'chartDefinition' in built &&
    isSameChart({ a: built.chartDefinition, b: chartDef })
  );
};

// A chart's dimensions as Studio groups them, in order: a date dimension is
// the chart's date axis, not one of them. One Studio can't group by ends
// the list, so the next isn't read in its place.
const guessDimensions = (
  chartDef: V5ChartDefinition,
): {
  dimensions: string[];
  dimensionOptions?: Studio.DimensionOptionsLookup;
} => {
  const dimensions: string[] = [];
  const dimensionOptions: Studio.DimensionOptionsLookup = {};
  // A matrix's secondary row dimension is a Table's second dimension, its
  // date columns (if any) the Table's columns
  const savedDimensions = isSecondaryDimensionAInEffect(chartDef)
    ? [chartDef.dimensionA, chartDef.secondaryDimensionA, chartDef.dimensionB]
    : [chartDef.dimensionA, chartDef.dimensionB];
  for (const saved of savedDimensions) {
    if (!saved || saved.fieldType === 'date') {
      continue;
    }
    const read = readDimension(saved);
    if (!read) {
      break;
    }
    dimensions.push(read.field);
    if (read.options) {
      dimensionOptions[read.field] = read.options;
    }
  }
  return {
    dimensions,
    ...(_.isEmpty(dimensionOptions) ? {} : { dimensionOptions }),
  };
};

const isStudioCompareType = (
  compareType: CompareType | undefined,
): compareType is 'previous' | 'lastYear' =>
  compareType === 'previous' || compareType === 'lastYear';

const SMART_CARD_SHOWS: MatrixCellType[] = ['percentDelta', 'delta', 'both'];

// A Number or Line block's comparison, as the gadget stores it: its
// compareType and interval and, on a smart card, what the badge shows. On a
// report, a smart card or unsplit time series without one of its own shows
// the report's. Older smart cards have what the badge shows misspelled
// (smartCardComparision), which useSmartCardData still reads.
const guessComparison = ({
  chartDef,
  comparison,
  isReportGadget,
}: {
  chartDef: V5ChartDefinition;
  comparison: PersistedComparisonType | undefined;
  isReportGadget: boolean;
}): Studio.BlockComparison | undefined => {
  const [first] = chartDef.series as (V5ChartDefinitionSeries & {
    smartCardComparision?: V5ChartDefinitionSeries['smartCardComparison'];
  })[];
  const show = first?.smartCardComparison ?? first?.smartCardComparision;
  const shown =
    chartDef.gadgetType === 'smartCard' &&
    show &&
    SMART_CARD_SHOWS.includes(show)
      ? { show: show as NonNullable<Studio.BlockComparison['show']> }
      : {};
  if (!comparison) {
    const drawsReportComparison =
      chartDef.gadgetType === 'smartCard' ||
      (chartDef.gadgetType === 'timeSeries' && !chartDef.dimensionB);
    return isReportGadget && drawsReportComparison
      ? { compareType: 'report', ...shown }
      : undefined;
  }
  if (!isStudioCompareType(comparison.compareType)) {
    return undefined;
  }
  const interval =
    comparison.compareType === 'previous' &&
    comparison.compareByInterval &&
    comparison.compareByInterval !== 'day'
      ? comparison.compareByInterval
      : undefined;
  return {
    compareType: comparison.compareType,
    ...(interval ? { interval } : {}),
    ...shown,
  };
};

// A records Table's settings from a simple grid
const guessRecordsCard = (
  chartDef: V5ChartDefinition,
): Studio.GadgetSidecarCard | undefined => {
  const config = chartDef.simpleGridConfig;
  if (!config) {
    return undefined;
  }
  const records: Studio.TableRecords = _.omitBy(
    {
      dataType: config.dataType || undefined,
      columns: getGridColumns(config),
      sort: config.sort && config.sort.length > 0 ? config.sort : undefined,
      isLinksDisabled: config.disableLinks || undefined,
    },
    _.isUndefined,
  );
  return {
    visType: 'table',
    name: chartDef.name,
    ...(chartDef.description ? { description: chartDef.description } : {}),
    table: _.omitBy(
      {
        rows: 'records' as const,
        records,
        gridSizing:
          config.gridSizing === 'fitContent' ? config.gridSizing : undefined,
      },
      _.isUndefined,
    ),
  };
};

// The Studio settings closest to a chart definition (and the gadget's
// comparison): its type, metrics, text dimensions, comparison and chart
// options. Whether they reproduce it is checked separately. A series without
// a metric has no block metric to be: saved, its gap would be dropped.
export const guessSidecarCard = ({
  chartDef: saved,
  comparison,
  isReportGadget = false,
}: {
  chartDef: V5ChartDefinition;
  comparison?: PersistedComparisonType;
  // A report gadget without a comparison of its own may show the report's
  isReportGadget?: boolean;
}): Studio.GadgetSidecarCard | undefined => {
  if (saved.gadgetType === 'simpleGrid') {
    return guessRecordsCard(saved);
  }
  const chartDef = withoutMetriclessSeries(saved);
  const visType =
    chartDef.gadgetType === 'smartCard'
      ? 'number'
      : getVisTypeForGadgetType(chartDef.gadgetType);
  if (!visType) {
    return undefined;
  }
  const { dimensions, dimensionOptions } = guessDimensions(chartDef);
  const blockComparison =
    visType === 'number' || visType === 'line'
      ? guessComparison({ chartDef, comparison, isReportGadget })
      : undefined;
  // A series asking its own question is a variant, keyed by its id (see
  // seriesVariants), where the block can draw one. So is a metric drawn
  // again asking the same one (as duplicateSeries makes), so each keeps its
  // own name and type.
  // A matrix's sort, limit, columns and metric options
  const tableOptions =
    visType === 'table'
      ? guessTableOptions({ chartDef, dimensions, comparison, isReportGadget })
      : undefined;
  const canVary = supportsSeriesVariants({
    visType,
    table: tableOptions,
    dimensions,
  });
  const seriesVariants: Record<string, Studio.SeriesVariant> = {};
  const plainMetricIds = new Set<string>();
  const metricIds = chartDef.series.map((s, index) => {
    const queryOverrides = canVary
      ? toQueryOverrides({ metricId: s.metricId, ...s.queryOverrides })
      : undefined;
    const isRepeat = canVary && plainMetricIds.has(s.metricId);
    if (!queryOverrides && !isRepeat) {
      plainMetricIds.add(s.metricId);
      return s.metricId;
    }
    const key =
      s.id && !seriesVariants[s.id] && !plainMetricIds.has(s.id)
        ? s.id
        : `${s.metricId}-${index}`;
    seriesVariants[key] = { metricId: s.metricId, ...queryOverrides };
    return key;
  });
  const variants = _.isEmpty(seriesVariants) ? {} : { seriesVariants };
  const chart = guessChartOptions({
    chartDef,
    content: { visType, metricIds, ...variants, dimensions, dimensionOptions },
  });
  // Its metric options keyed by series, where some are variants
  const table =
    tableOptions && !_.isEmpty(seriesVariants)
      ? guessTableOptions({
          chartDef,
          dimensions,
          comparison,
          isReportGadget,
          keys: metricIds,
        })
      : tableOptions;
  return {
    visType,
    metricIds,
    ...variants,
    name: chartDef.name,
    ...(chartDef.description ? { description: chartDef.description } : {}),
    ...(dimensions.length > 0 ? { dimensions } : {}),
    ...(dimensionOptions ? { dimensionOptions } : {}),
    ...(blockComparison ? { comparison: blockComparison } : {}),
    ...(chart ? { chart } : {}),
    ...(table ? { table } : {}),
    ...(chartDef.gadgetType === 'fixedTrendCard'
      ? { trend: { period: 'month' as const } }
      : {}),
  };
};
